import { EventEmitter } from "eventemitter3";
import { DomLayerElementInterface } from "../config/DomLayerElementInterface";
import { DomElementFrameInterface } from "../config/DomElementFrameInterface";
import { DomElementTypes } from "./DomElementTypes";
import { DomLayer } from "../dom-layer";

export abstract class DomElementBase extends EventEmitter {
    public imageDirPath: string;
    public element: HTMLElement | null;
    public type: string;
    public name: string;
    public title: string | undefined;
    public width: number;
    public height: number;
    public padding: number;
    public asset?: string;
    public stateVariable: any[];
    public animation?: string;
    public retainFocus?: boolean;
    public location: string;
    public locationIndex: number; // zero-based; order in which the element has been added to the specific position; e.g. the first element to be added top-right
    public absolute?: boolean;
    public frame: string;
    public optionalParameters: any;
    public offset: any;
    public xPos: number = 0; // used to store the value while calculating x position
    public hideElement: boolean = false; // allows the element to be hidden, depending upon state variables
    protected _title?: string;
    protected smallerThanBreakpoint: boolean;
    protected domLayer: DomLayer;
    protected timeout_enableElement: number;

    constructor(
        domLayer: DomLayer,
        imageDirPath: string,
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface,
        frames: DomElementFrameInterface[]
    ) {
        super();
        this.domLayer = domLayer;
        this.imageDirPath = imageDirPath;
        this.init(options, defaultOptions, frames);
    }

    public updateStateVariable(state: any[]) {}

    public get currentTitle(): string | undefined {
        return this._title;
    }

    public set currentTitle(value: string | undefined) {
        this._title = value;
        if (this.element && value && value !== "") {
            this.element.setAttribute("aria-label", value);
        }
    }

    public destroy() {
        if (this.timeout_enableElement) window.clearTimeout(this.timeout_enableElement);
        if (this.element) {
            this.element.style.backgroundImage = "none";
            this.element = null;
        }
    }

    protected init(
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface,
        frames: DomElementFrameInterface[]
    ) {
        this.smallerThanBreakpoint = DomLayer.isSmallerThanBreakpoint();
        this.applyOptions(options, defaultOptions, frames);

        let domElementType: string = this.type;
        if (domElementType === DomElementTypes.TOGGLE) domElementType = DomElementTypes.BUTTON;

        this.element = document.createElement(domElementType);
        this.element.id = this.name;
        this.element.style.width = this.width + this.padding * 2 + "px";
        this.element.style.height = this.height + this.padding * 2 + "px";
        this.element.className = "dl-element";

        this.currentTitle = options.title;
        if (this.currentTitle !== "") this.element.setAttribute("aria-label", this.currentTitle);
        // ARIA attributes ensure that button title and hint updates are announced properly
        // Chrome doesn't need aria-live to work as intended; in fact it just gets more confused.
        const isChrome = navigator.userAgent.toLowerCase().indexOf("chrome") > -1;
        const ariaLive = isChrome ? "polite" : "off";
        if (this.currentTitle !== "") {
            this.element.setAttribute("aria-live", ariaLive);
            this.element.setAttribute("aria-relevant", "all");
        }

        // add the image
        const imagePath: string = this.imageDirPath + this.asset;
        this.element.style.backgroundImage = "url(" + imagePath + ")";
        this.element.style.padding = this.padding + "px";

        this.timeout_enableElement = window.setTimeout(this.enableElement.bind(this), 100);
    }

    pulse(pulse?: boolean) {
        if (this.element) {
            if (typeof pulse === "undefined") pulse = true;
            if (pulse) this.element.classList.add("pulse");
            else this.element.classList.remove("pulse");
        }
    }

    protected enableElement() {}

    protected applyOptions(
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface,
        frames: DomElementFrameInterface[]
    ) {
        this.applyDefaultOptions(options, defaultOptions);

        const frameData: DomElementFrameInterface = this.getFrameByName(options.frame, frames);
        this.applyFrameInfo(options, frameData);
        const asset: string = this.getAssetWithSuffix(options.asset, frameData);

        this.type = options.type;
        this.name = options.name;
        this.width = options.width;
        this.height = options.height;
        this.padding = options.padding;
        this.title = options.title;
        this.asset = asset;
        this.stateVariable = options.stateVariable;
        this.animation = options.animation;
        this.retainFocus = options.retainFocus;
        this.location = options.location;
        this.locationIndex = options.locationIndex;
        this.absolute = options.absolute;
        this.frame = options.frame;
        this.optionalParameters = options.optionalParameters;
        this.offset = options.offset;
    }

    protected applyDefaultOptions(
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface
    ) {
        if (!options.name) options.name = defaultOptions.name;
        if (!options.asset) options.asset = defaultOptions.asset;
        if (!options.title) options.title = defaultOptions.title;
        if (!options.layout) options.layout = defaultOptions.layout;
        if (!options.animation) options.animation = defaultOptions.animation;
        if (!options.retainFocus) options.retainFocus = defaultOptions.retainFocus;
        if (!options.location) options.location = defaultOptions.location;
        if (!options.absolute) options.absolute = defaultOptions.absolute;
        if (!options.stateVariable) options.stateVariable = defaultOptions.stateVariable;
        if (!options.locationIndex) options.locationIndex = defaultOptions.locationIndex;
        if (!options.frame) options.frame = defaultOptions.frame;
        if (!options.optionalParameters)
            options.optionalParameters = defaultOptions.optionalParameters;
        if (!options.offset) options.offset = defaultOptions.offset;
    }

    protected applyFrameInfo(
        options: DomLayerElementInterface,
        frameData: DomElementFrameInterface
    ) {
        (options.width = this.smallerThanBreakpoint
            ? frameData.breakPoints.small.width
            : frameData.breakPoints.large.width),
            (options.height = this.smallerThanBreakpoint
                ? frameData.breakPoints.small.height
                : frameData.breakPoints.large.height),
            (options.padding = this.smallerThanBreakpoint
                ? frameData.breakPoints.small.padding
                : frameData.breakPoints.large.padding);
    }

    protected getFrameByName(
        name: string,
        frames: DomElementFrameInterface[]
    ): DomElementFrameInterface {
        for (let i: number = 0; i < frames.length; i++) {
            if (frames[i].name === name) {
                return frames[i];
            }
        }
        return frames[0];
    }

    protected getAssetWithSuffix(asset: string, frameData: DomElementFrameInterface) {
        let ext: string = "";
        let filename: string = asset;
        const index: number = asset.indexOf(".");

        if (index !== -1) {
            ext = asset.substring(index);
            filename = asset.substring(0, index);
        }

        if (this.smallerThanBreakpoint) {
            return filename + frameData.breakPoints.small.assetSuffix + ext;
        } else {
            return filename + frameData.breakPoints.large.assetSuffix + ext;
        }
    }
}
